import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { FiCalendar, FiDownload, FiInfo, FiPrinter, FiRefreshCw } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { getOperationalDashboard } from 'services/dashboard.service';
import useDashboardReport from 'hooks/useDashboardReport';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import {
  DOMAIN_CONFIG,
  DOMAIN_WIDGET_ORDER,
  OPERATIONAL_REPORT_TABS,
  WIDGET_DEFINITIONS,
} from './operationalDashboard.config';
import './operationalDashboard.scss';

const COLORS = ['#07b89f', '#f6c85f', '#6f74dd', '#d46ad2', '#ff7b55', '#ef476f', '#4b9fea', '#8bc34a'];
const DEFAULT_RANGE = () => ({
  date_from: moment().startOf('year').format('YYYY-MM-DD'),
  date_to: moment().format('YYYY-MM-DD'),
});

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatNumber = (value, digits = 0) => numberValue(value).toLocaleString('vi-VN', { maximumFractionDigits: digits });
const formatMoney = (value, digits = 0) => formatNumber(numberValue(value) / 1000000, digits);
const formatPercent = (value) => `${formatNumber(value, 2)}%`;
const metricValue = (value, raw, signed = false) => {
  const current = numberValue(value);
  const text = raw ? formatNumber(Math.abs(current), 2) : formatMoney(Math.abs(current), 2);
  return signed && current < 0 ? `(${text})` : text;
};

const WidgetExplanation = ({ definition }) => (
  <details className='bc_od_explanation'>
    <summary><FiInfo /> Diễn giải số liệu</summary>
    <dl>
      <div><dt>Quyền truy cập</dt><dd><code>{definition.note.permission}</code></dd></div>
      <div><dt>Nguồn dữ liệu</dt><dd>{definition.note.source}</dd></div>
      <div><dt>Cách tính</dt><dd>{definition.note.formula}</dd></div>
      <div><dt>Thời điểm/phạm vi</dt><dd>{definition.note.cutoff}</dd></div>
    </dl>
  </details>
);

const Widget = ({ definition, children }) => (
  <section className='bc_od_widget' style={{ gridColumn: `span ${definition.span || 6}` }}>
    <header><h3>{definition.title}</h3>{definition.type !== 'table' ? <span>({definition.raw ? 'Số lượng' : 'Triệu đồng'})</span> : null}</header>
    <div className='bc_od_widget_body'>{children}</div>
    <WidgetExplanation definition={definition} />
  </section>
);

const EmptyState = () => <div className='bc_od_empty'>Không có dữ liệu.</div>;
const CostDataWarning = () => (
  <div className='bc_od_empty'>Chưa đủ dữ liệu giá vốn để tính chính xác.</div>
);
const hasUnavailableValues = (rows = [], valueKey = 'amount') => (
  rows.length > 0 && rows.every((row) => row?.[valueKey] == null)
);

const DebtSummary = ({ data = {} }) => {
  const total = numberValue(data.total);
  const current = numberValue(data.current);
  const overdue = numberValue(data.overdue);
  const unclassified = numberValue(data.unclassified);
  const hasDueData = data.due_data_available === true || numberValue(data.due_data_available) === 1;
  const ratio = total > 0 ? Math.min(100, current * 100 / total) : 0;
  return (
    <div className='bc_od_summary'>
      <strong>{formatMoney(total, 2)}</strong><small>TỔNG</small>
      {hasDueData ? (
        <>
          <div><p><b>{formatMoney(current, 2)}</b><span>TRONG HẠN</span></p><p><b className='is-danger'>{formatMoney(overdue, 2)}</b><span>QUÁ HẠN</span></p></div>
          <i><b style={{ width: `${ratio}%` }} /></i>
          {unclassified > 0 ? <em>{formatMoney(unclassified, 2)} triệu chưa khai báo hạn nợ</em> : null}
        </>
      ) : (
        <div><p><b>{formatMoney(total, 2)}</b><span>CHƯA PHÂN LOẠI HẠN NỢ</span></p><p><b>—</b><span>CHƯA ĐỦ NGÀY ĐẾN HẠN</span></p></div>
      )}
    </div>
  );
};

const MetricSummary = ({ data = {} }) => (
  <div className='bc_od_metric_summary'>
    <div><strong>{formatMoney(data.revenue, 2)}</strong><span>DOANH THU</span></div>
    <div><strong>{formatMoney(data.expense, 2)}</strong><span>CHI PHÍ</span></div>
    <div><strong className={numberValue(data.profit) < 0 ? 'is-danger' : ''}>{metricValue(data.profit, false, true)}</strong><span>LỢI NHUẬN</span></div>
  </div>
);

const InventorySummary = ({ data = {} }) => {
  const hasCompleteCost = data.cost_data_complete === true || numberValue(data.cost_data_complete) === 1;
  const knownCurrentValue = numberValue(data.known_current_value);
  const missingCostRows = numberValue(data.missing_cost_row_count);
  return (
    <div className='bc_od_inventory_summary'>
      <strong>{hasCompleteCost ? formatMoney(data.current_value, 2) : knownCurrentValue ? formatMoney(knownCurrentValue, 2) : 'Chưa đủ giá vốn'}</strong>
      <span>{hasCompleteCost ? 'GIÁ TRỊ TỒN HIỆN TẠI' : knownCurrentValue ? 'GIÁ TRỊ ĐÃ XÁC ĐỊNH' : 'CHƯA ĐỦ DỮ LIỆU GIÁ VỐN'}</span>
      {hasCompleteCost ? (
        <div><p><b>{formatMoney(data.previous_value, 2)}</b><small>KỲ TRƯỚC</small></p><p><b className={numberValue(data.change_percent) < 0 ? 'is-danger' : 'is-positive'}>{formatPercent(data.change_percent)}</b><small>SO VỚI KỲ TRƯỚC</small></p></div>
      ) : <div className='bc_od_empty'>{missingCostRows ? `${formatNumber(missingCostRows)} dòng tồn còn thiếu giá vốn; phần giá trị trên chưa gồm các dòng này.` : 'Chưa đủ dữ liệu giá vốn để tính chính xác.'}</div>}
      <em>{formatNumber(data.current_quantity, 2)} đơn vị tồn vật lý</em>
    </div>
  );
};

const LineChart = ({ rows = [], valueKey = 'amount', raw, signed }) => {
  if (!rows.length) return <EmptyState />;
  const values = rows.map((row) => numberValue(row[valueKey]));
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const range = Math.max(1, max - min);
  const points = rows.map((row, index) => {
    const x = rows.length === 1 ? 50 : index * 100 / (rows.length - 1);
    const y = 92 - ((numberValue(row[valueKey]) - min) / range) * 78;
    return `${x},${y}`;
  }).join(' ');
  return (
    <div className='bc_od_line_chart'>
      <svg viewBox='0 0 100 100' preserveAspectRatio='none'><polyline points={points} /></svg>
      <div className='bc_od_line_labels'>{rows.map((row, index) => <span key={`${row.period_key || row.label}-${index}`} title={metricValue(row[valueKey], raw, signed)}>{row.label || row.period_label}</span>)}</div>
    </div>
  );
};

const BarChart = ({ rows = [], labelKey = 'name', valueKey = 'amount', raw }) => {
  if (!rows.length) return <EmptyState />;
  if (hasUnavailableValues(rows, valueKey)) return <CostDataWarning />;
  const total = rows.reduce((sum, row) => sum + Math.abs(numberValue(row[valueKey])), 0);
  const max = Math.max(1, ...rows.map((row) => Math.abs(numberValue(row[valueKey]))));
  return (
    <div className='bc_od_bar_chart'>
      {rows.map((row, index) => {
        const value = numberValue(row[valueKey]);
        const share = row.share_percent == null ? (total ? Math.abs(value) * 100 / total : 0) : numberValue(row.share_percent);
        return (
          <div key={`${row[labelKey] || index}-${index}`}>
            <span title={row[labelKey]}>{row[labelKey] || 'Chưa xác định'}</span>
            <i><b style={{ width: `${Math.max(2, Math.abs(value) * 100 / max)}%` }} /></i>
            <em>{metricValue(value, raw)} <small>({formatPercent(share)})</small></em>
          </div>
        );
      })}
    </div>
  );
};

const ColumnChart = ({ rows = [], valueKey = 'amount', raw }) => {
  if (!rows.length) return <EmptyState />;
  if (hasUnavailableValues(rows, valueKey)) return <CostDataWarning />;
  const max = Math.max(1, ...rows.map((row) => Math.abs(numberValue(row[valueKey]))));
  return (
    <div className='bc_od_column_chart'>
      {rows.map((row, index) => <div key={`${row.period_key || row.label}-${index}`}><em>{metricValue(row[valueKey], raw)}</em><i style={{ height: `${Math.max(3, Math.abs(numberValue(row[valueKey])) * 100 / max)}%` }} /><span>{row.label || row.period_label}</span></div>)}
    </div>
  );
};

const DonutChart = ({ rows = [], valueKey = 'amount', raw }) => {
  if (hasUnavailableValues(rows, valueKey)) return <CostDataWarning />;
  if (!rows.length || !rows.some((row) => numberValue(row[valueKey]) > 0)) return <EmptyState />;
  const total = rows.reduce((sum, row) => sum + Math.max(0, numberValue(row[valueKey])), 0);
  let current = 0;
  const parts = rows.map((row, index) => {
    const start = current;
    current += Math.max(0, numberValue(row[valueKey])) * 100 / total;
    return `${COLORS[index % COLORS.length]} ${start}% ${current}%`;
  });
  return (
    <div className='bc_od_donut_wrap'>
      <div className='bc_od_donut' style={{ background: `conic-gradient(${parts.join(',')})` }}><b>{metricValue(total, raw)}</b><span>TỔNG</span></div>
      <div className='bc_od_legend'>{rows.map((row, index) => <p key={`${row.label}-${index}`}><i style={{ background: COLORS[index % COLORS.length] }} /><span>{row.label || row.name}</span><b>{formatPercent(numberValue(row[valueKey]) * 100 / total)}</b></p>)}</div>
    </div>
  );
};

const TableWidget = ({ rows = [], columns = [] }) => {
  if (!rows.length) return <EmptyState />;
  const render = (value, type) => type === 'money' ? formatMoney(value, 2) : type === 'percent' ? formatPercent(value) : type === 'number' ? formatNumber(value, 2) : value || '—';
  return (
    <div className='bc_od_table_wrap'><table><thead><tr>{columns.map(([key, label]) => <th key={key}>{label}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr key={`${row.id || row.code || row.document_no || index}-${index}`}>{columns.map(([key, , type]) => <td key={key} title={`${row[key] ?? ''}`}>{render(row[key], type)}</td>)}</tr>)}</tbody></table></div>
  );
};

const DualChart = ({ rows = [], mode }) => {
  if (!rows.length) return <EmptyState />;
  const firstKey = mode === 'warehouse' ? 'quantity' : 'quantity';
  const secondKey = mode === 'warehouse' ? 'amount' : 'amount';
  if (hasUnavailableValues(rows, secondKey)) return <CostDataWarning />;
  const maxFirst = Math.max(1, ...rows.map((row) => Math.abs(numberValue(row[firstKey]))));
  const maxSecond = Math.max(1, ...rows.map((row) => Math.abs(numberValue(row[secondKey]))));
  return (
    <div className='bc_od_dual_chart'>
      {rows.map((row, index) => <div key={`${row.label || row.name}-${index}`}><span>{row.label || row.period_label || row.name}</span><i><b style={{ width: `${Math.abs(numberValue(row[firstKey])) * 100 / maxFirst}%` }} /></i><i className='is-value'><b style={{ width: `${Math.abs(numberValue(row[secondKey])) * 100 / maxSecond}%` }} /></i><em>{formatNumber(row[firstKey], 2)} / {formatMoney(row[secondKey], 2)} tr</em></div>)}
      <footer><span><i />SỐ LƯỢNG</span><span><i />GIÁ TRỊ</span></footer>
    </div>
  );
};

const ColumnLineChart = ({ rows = [] }) => {
  if (!rows.length) return <EmptyState />;
  const max = Math.max(1, ...rows.map((row) => Math.abs(numberValue(row.amount))));
  const points = rows.map((row, index) => `${rows.length === 1 ? 50 : index * 100 / (rows.length - 1)},${92 - Math.min(100, Math.max(0, numberValue(row.ratio))) * 0.78}`).join(' ');
  return <div className='bc_od_combo_chart'><svg viewBox='0 0 100 100' preserveAspectRatio='none'><polyline points={points} /></svg><div>{rows.map((row, index) => <p key={`${row.period_key}-${index}`}><em>{formatMoney(row.amount, 2)}</em><i style={{ height: `${Math.max(3, Math.abs(numberValue(row.amount)) * 100 / max)}%` }} /><span>{row.label || row.period_label}</span></p>)}</div></div>;
};

const renderWidget = (definition, data) => {
  switch (definition.type) {
    case 'debt-summary': return <DebtSummary data={data} />;
    case 'metric-summary': return <MetricSummary data={data} />;
    case 'inventory-summary': return <InventorySummary data={data} />;
    case 'line': return <LineChart rows={data} valueKey={definition.valueKey} raw={definition.raw} signed={definition.signed} />;
    case 'bar': return <BarChart rows={data} labelKey={definition.labelKey} valueKey={definition.valueKey} raw={definition.raw} />;
    case 'column': return <ColumnChart rows={data} valueKey={definition.valueKey} raw={definition.raw} />;
    case 'donut': return <DonutChart rows={data} valueKey={definition.valueKey} raw={definition.raw} />;
    case 'table': return <TableWidget rows={data} columns={definition.columns} />;
    case 'column-line': return <ColumnLineChart rows={data} />;
    case 'dual-line': return <DualChart rows={data} mode='trend' />;
    case 'dual-bar': return <DualChart rows={data} mode='warehouse' />;
    default: return <EmptyState />;
  }
};

const csvCell = (value) => `"${`${value ?? ''}`.replace(/"/g, '""')}"`;
const EMPTY_DATA = { widgets: {}, permissions: [] };

const OperationalDashboardPage = ({ domain }) => {
  const history = useHistory();
  const config = DOMAIN_CONFIG[domain];
  const [scope, setScope] = useState(getPortalScope);
  const [range, setRange] = useState(DEFAULT_RANGE);
  const request = useCallback(
    (signal) => getOperationalDashboard(config.endpoint, { ...range, ...scope }, { signal }),
    [config.endpoint, range, scope],
  );
  const { data, loading, error, load } = useDashboardReport(request, EMPTY_DATA);
  const widgets = data.widgets || {};
  const permissions = data.permissions || [];
  useEffect(() => {
    const handler = (event) => setScope(event?.detail || getPortalScope());
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, handler);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, handler);
  }, []);

  const visibleKeys = useMemo(() => DOMAIN_WIDGET_ORDER[domain].filter((key) => permissions.includes(key)), [domain, permissions]);
  const periodLabel = `${moment(range.date_from).format('DD/MM/YYYY')} - ${moment(range.date_to).format('DD/MM/YYYY')}`;

  const applyPreset = (preset) => {
    const end = moment();
    const start = preset === 'month' ? moment().startOf('month') : preset === 'quarter' ? moment().startOf('quarter') : moment().startOf('year');
    setRange({ date_from: start.format('YYYY-MM-DD'), date_to: end.format('YYYY-MM-DD') });
  };

  const exportCsv = () => {
    const rows = [['Báo cáo', config.title], ['Kỳ', periodLabel], []];
    visibleKeys.forEach((key) => {
      rows.push([WIDGET_DEFINITIONS[key].title], ['Quyền', WIDGET_DEFINITIONS[key].note.permission]);
      const value = widgets[key];
      if (Array.isArray(value) && value.length) {
        const keys = Object.keys(value[0]);
        rows.push(keys, ...value.map((item) => keys.map((field) => item[field])));
      } else if (value && typeof value === 'object') {
        rows.push(...Object.entries(value));
      }
      rows.push([]);
    });
    const blob = new Blob([`\ufeff${rows.map((row) => row.map(csvCell).join(',')).join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${config.exportName}-${range.date_to}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className='bw_main_wrapp bc_od_page'>
      <nav className='bc_od_tabs' aria-label='Báo cáo tổng quan'>{OPERATIONAL_REPORT_TABS.map((tab) => <button type='button' key={tab.key} className={tab.key === domain ? 'is-active' : ''} onClick={() => history.push(tab.path)}>{tab.label}</button>)}</nav>
      <header className='bc_od_header'><div><h2>{config.title}</h2><p>{scope.store_name || scope.business_name || scope.company_name || 'Toàn hệ thống'} · {periodLabel}</p></div><div><button type='button' onClick={() => window.print()}><FiPrinter /> In</button><button type='button' onClick={exportCsv} disabled={!visibleKeys.length}><FiDownload /> Xuất khẩu</button></div></header>
      <div className='bc_od_filterbar'><div><button type='button' onClick={() => applyPreset('year')}>Đầu năm đến hiện tại</button><button type='button' onClick={() => applyPreset('quarter')}>Quý này</button><button type='button' onClick={() => applyPreset('month')}>Tháng này</button></div><div><FiCalendar /><input aria-label='Từ ngày' type='date' value={range.date_from} max={range.date_to} onChange={(event) => setRange((current) => ({ ...current, date_from: event.target.value }))} /><span>đến</span><input aria-label='Đến ngày' type='date' value={range.date_to} min={range.date_from} onChange={(event) => setRange((current) => ({ ...current, date_to: event.target.value }))} /><button type='button' title='Tải lại' onClick={load}><FiRefreshCw /></button></div></div>
      {error ? <div className='bc_od_error'>{error}<button type='button' onClick={load}>Thử lại</button></div> : null}
      {loading ? <div className='bc_od_loading'>Đang tổng hợp dữ liệu báo cáo...</div> : null}
      {!loading && !error && !visibleKeys.length ? <div className='bc_od_no_permission'><FiInfo /><h3>Chưa có biểu đồ được cấp quyền</h3><p>Hãy cấp key quyền của từng biểu đồ cho nhóm người dùng. Quyền mở trang và quyền xem biểu đồ được quản lý riêng.</p></div> : null}
      <main className='bc_od_grid'>{visibleKeys.map((key) => <Widget key={key} definition={WIDGET_DEFINITIONS[key]}>{renderWidget(WIDGET_DEFINITIONS[key], widgets[key])}</Widget>)}</main>
    </div>
  );
};

export default OperationalDashboardPage;
